/**
 * ArchiveRateDialog: the shared plain-words confirm for archiving (or
 * restoring) ONE rate row (0138 rates administration; founder
 * feedback 2026-07-10: ghost rates need a way out of the matrix).
 *
 * Two callers (the SetRateDialog two-surface pattern):
 *   - RatesCard (client record > Pre-billing, the earlier-rates
 *     expansion): onDone seeds the per-client history cache from the
 *     PATCH's echo;
 *   - ClientRatesTab (Admin console grid): onDone invalidates the
 *     tenant-wide overview query so the grid refetches.
 *
 * The copy states the money guarantee in plain words: new time will no
 * longer bill at an archived rate; anything already billed keeps it
 * (events snapshotted their rate at write time — nothing here rewrites
 * money).
 */

import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/primitives/Button';
import { Dialog } from '@/components/primitives/Dialog';
import { ApiError } from '@/lib/api';
import { contactSentence, saveFailureText, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import {
  formatMoney,
  patchClientRateArchived,
  type ClientRateRowWire,
} from '@/lib/clientRates';
import { proseDate } from '@/lib/proseDate';

function saveErrorMessage(err: unknown, contact: SupportContact): string {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return contactSentence("Changing rates isn't part of your access. Ask {contact_billing} to make this change.", contact);
    }
    if (err.status === 404) return 'That rate is already gone. Reload the page to see the current rates.';
  }
  return (
    saveFailureText(err, contact) ??
    contactSentence("That didn't save. Try again, and if it happens again, tell {contact_system}.", contact)
  );
}

export interface ArchiveRateDialogProps {
  clientId: string;
  /** The row being retired / brought back. */
  rate: { id: string; rate: string; effective_from: string };
  /** The band's label for the title ("Care management"). */
  bandLabel: string;
  /** Dialog title override (the SetRateDialog discipline — the Admin
   *  grid passes the client's name so the operator always sees whose
   *  rate is open). */
  title?: string;
  /** false = the row is archived and this dialog restores it. */
  archived: boolean;
  /** Receives the PATCH's echoed refreshed history (the caller decides
   *  whether to seed a cache or refetch an aggregate). */
  onDone: (rates: ClientRateRowWire[]) => void;
  onClose: () => void;
}

export function ArchiveRateDialog({
  clientId,
  rate,
  bandLabel,
  title,
  archived,
  onDone,
  onClose,
}: ArchiveRateDialogProps): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const [error, setError] = useState<string | null>(null);
  const contact = useSupportContact();

  // The request is built where Save is pressed and travels as the
  // mutation's variables: React Query hands a mutation its options in an
  // effect, so a mutationFn reading this render's values could send an
  // earlier render's.
  const mut = useMutation<ClientRateRowWire[], unknown, { rateId: string; archived: boolean }>({
    mutationFn: (v) => patchClientRateArchived(tenantSlug, clientId, v.rateId, v.archived),
    onSuccess: (rates) => {
      onDone(rates);
      onClose();
    },
    onError: (err) => setError(saveErrorMessage(err, contact)),
  });

  return (
    <Dialog
      title={title ?? (archived ? `Archive ${bandLabel} rate` : `Restore ${bandLabel} rate`)}
      role="alertdialog"
      size="sm"
      testId="archive-rate-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={mut.isPending}
            disabled={mut.isPending}
            data-testid="archive-rate-confirm"
            onClick={() => mut.mutate({ rateId: rate.id, archived })}
          >
            {archived ? 'Archive rate' : 'Restore rate'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2 py-2 text-sm text-ink">
        <p data-testid="archive-rate-copy">
          {archived ? (
            <>
              Archive this rate? New time will no longer bill at it. Anything already billed
              keeps it.
            </>
          ) : (
            <>Restore this rate? New time can bill at it again from its date.</>
          )}
        </p>
        <p className="text-xs text-ink-soft">
          {formatMoney(rate.rate)} / hour from {proseDate(rate.effective_from)}
        </p>
        {error !== null && (
          <p role="alert" data-testid="archive-rate-error" className="text-xs text-pepper">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
